/**
 * La aritmetica del cliente, espejo de `Domain/Trading/TradeArithmetic.cs`.
 *
 * SPEC 15.5: el formulario calcula riesgo, objetivo, RR y R EN VIVO mientras
 * escribes. Ver el RR antes de entrar es exactamente el momento en que sirve
 * saberlo, y el R mientras escribes el precio de salida evita el dedazo de un
 * decimal, que es el error mas facil de cometer y el mas difi­cil de detectar
 * despues.
 *
 * Lo que sale de aqui es una PREVISUALIZACION y se etiqueta como tal (SPEC 15.1):
 * el servidor es la verdad aritmetica, y al recibir su respuesta esto se
 * descarta.
 *
 * Las dos implementaciones se comparan con `docs/test-vectors/arithmetic.json`,
 * que es la unica definicion (SPEC 10). Si divergen, `pips.test.ts` lo dice.
 */

export type Direction = "buy" | "sell";
export type TradeOutcome = "win" | "loss" | "breakEven";

/** El mismo numero que declara `rounding.decimals` en el archivo de vectores. */
export const DECIMALS = 2;

/**
 * AwayFromZero, como en C#. `Math.round` de JavaScript redondea .5 SIEMPRE hacia
 * arriba, asi que -1.005 daria -1.00 en vez de -1.01 y las dos aritmeticas se
 * separarian en los negativos.
 *
 * Se opera sobre el valor escalado y con un epsilon: 1.005 no es exactamente
 * 1.005 en coma flotante binaria, y sin el epsilon el redondeo cae del lado
 * equivocado.
 */
export function round(value: number): number {
  const factor = 10 ** DECIMALS;
  const scaled = value * factor;
  const sign = scaled < 0 ? -1 : 1;
  const rounded = Math.floor(Math.abs(scaled) + 0.5 + Number.EPSILON * Math.abs(scaled));

  return (sign * rounded) / factor;
}

/** pips(p1, p2) = |p1 - p2| / pipSize. Siempre >= 0: es una distancia. */
export function distance(priceA: number, priceB: number, pipSize: number): number {
  return round(Math.abs(priceA - priceB) / pipSize);
}

/**
 * direccional(e, s, d) = (d == buy ? s - e : e - s) / pipSize. CON signo.
 * En una venta, bajar es ganar.
 */
export function directional(
  entry: number,
  exit: number,
  direction: Direction,
  pipSize: number,
): number {
  const difference = direction === "buy" ? exit - entry : entry - exit;

  return round(difference / pipSize);
}

/**
 * Divide y devuelve NULL si el denominador es cero.
 *
 * SPEC 10, regla 2: null no es cero. `rMultiple` es null si no hubo stop y
 * `captureRate` es null si el MFE fue cero. "No cumpli nada" y "no lo revise"
 * son cosas distintas.
 */
export function ratio(numerator: number, denominator: number): number | null {
  return denominator === 0 ? null : round(numerator / denominator);
}

export function outcomeOf(resultPips: number): TradeOutcome {
  if (resultPips > 0) return "win";
  if (resultPips < 0) return "loss";

  return "breakEven";
}

export type RuleCheck = { followed: boolean; mandatory: boolean };

/** adherencia = cumplidas / marcadas, o NULL si no hay checklist. */
export function adherenceScore(ruleChecks: readonly RuleCheck[]): number | null {
  if (ruleChecks.length === 0) return null;

  const followed = ruleChecks.filter((check) => check.followed).length;

  return round(followed / ruleChecks.length);
}

/**
 * La adherencia y la regla innegociable son DOS senales separadas (SPEC 10,
 * regla 3). Un numero mezclado esconderia lo grave entre las buenas.
 */
export function hasBrokenMandatoryRule(ruleChecks: readonly RuleCheck[]): boolean {
  return ruleChecks.some((check) => check.mandatory && !check.followed);
}

export type PlanPreview = {
  plannedRiskPips: number;
  plannedRewardPips: number;
  plannedRiskReward: number | null;
  stopOnWrongSide: boolean;
  takeProfitOnWrongSide: boolean;
};

/**
 * Lo que el paso 2 del alta pinta mientras escribes (SPEC 15.5), incluido el
 * aviso de stop del lado equivocado ANTES de dejar avanzar.
 *
 * Un stop igual a la entrada significa "opero sin stop": riesgo 0, RR null, y no
 * es un error (SPEC 9).
 */
export function planPreview(
  plannedEntry: number,
  stopLoss: number,
  takeProfit: number,
  direction: Direction,
  pipSize: number,
): PlanPreview {
  const plannedRiskPips = distance(plannedEntry, stopLoss, pipSize);
  const plannedRewardPips = distance(plannedEntry, takeProfit, pipSize);

  return {
    plannedRiskPips,
    plannedRewardPips,
    plannedRiskReward: ratio(plannedRewardPips, plannedRiskPips),
    stopOnWrongSide: direction === "buy" ? stopLoss > plannedEntry : stopLoss < plannedEntry,
    takeProfitOnWrongSide:
      direction === "buy" ? takeProfit < plannedEntry : takeProfit > plannedEntry,
  };
}

export type ClosePreview = {
  resultPips: number;
  rMultiple: number | null;
  outcome: TradeOutcome;
  captureRate: number | null;
};

/**
 * Lo que el panel inferior de cierre pinta mientras escribes el precio de salida
 * (SPEC 15.5).
 *
 * El riesgo es el PLANIFICADO, no el real: el deslizamiento ya esta dentro del
 * resultado, porque este se mide desde la entrada real. Cobrarlo tambien en el
 * riesgo lo cobraria dos veces (SPEC 10, regla 1).
 */
export function closePreview(
  actualEntry: number,
  actualExit: number,
  direction: Direction,
  pipSize: number,
  plannedRiskPips: number,
  mfePips: number | null,
): ClosePreview {
  const resultPips = directional(actualEntry, actualExit, direction, pipSize);

  return {
    resultPips,
    rMultiple: ratio(resultPips, plannedRiskPips),
    outcome: outcomeOf(resultPips),
    captureRate: mfePips === null ? null : ratio(resultPips, mfePips),
  };
}

/* ========================================================================== */
/* El camino de los pips ESCRITOS (ADR 0007)                                  */
/* ========================================================================== */

/**
 * Cuanto pueden separarse los pips escritos de los derivados de los precios
 * antes de llamarlo desacuerdo. Es el mismo numero que declara
 * `typed.disagreeTolerancePips` en el archivo de vectores: medio pip es
 * redondeo, mas de medio pip es un dedazo en uno de los dos.
 */
export const DISAGREE_TOLERANCE_PIPS = 0.5;

/**
 * Los pips escritos y los derivados no cuadran. Se SENALA, no se corrige: uno
 * de los dos es un dedazo y saber cual es justo lo que hay que averiguar.
 */
export function pipsDisagree(typed: number, derived: number): boolean {
  return Math.abs(typed - derived) > DISAGREE_TOLERANCE_PIPS;
}

export type TypedPlanPreview = {
  plannedRiskPips: number;
  plannedRewardPips: number;
  plannedRiskReward: number | null;
};

/**
 * Lo que el alta pinta mientras se escriben los pips de riesgo y de objetivo.
 * Riesgo cero sigue siendo riesgo cero: sin pips de riesgo no hay RR, y no es
 * un cero (SPEC 10).
 */
export function typedPlanPreview(
  plannedRiskPips: number,
  plannedRewardPips: number,
): TypedPlanPreview {
  const risk = round(plannedRiskPips);
  const reward = round(plannedRewardPips);

  return {
    plannedRiskPips: risk,
    plannedRewardPips: reward,
    plannedRiskReward: ratio(reward, risk),
  };
}

/**
 * Lo que el cierre pinta mientras se escriben los pips ganados o perdidos, CON
 * signo. El R no cambia de definicion: `resultPips / plannedRiskPips`, con el
 * riesgo PLANIFICADO, y null cuando el riesgo es cero.
 */
export function typedClosePreview(
  resultPips: number,
  plannedRiskPips: number,
  mfePips: number | null,
): ClosePreview {
  const result = round(resultPips);

  return {
    resultPips: result,
    rMultiple: ratio(result, plannedRiskPips),
    outcome: outcomeOf(result),
    captureRate: mfePips === null ? null : ratio(result, mfePips),
  };
}

export type PipsTriplet = {
  plannedRiskPips: number | null;
  plannedRewardPips: number | null;
  resultPips: number | null;
};

export type Disagreement = {
  riskDisagree: boolean;
  rewardDisagree: boolean;
  resultDisagree: boolean;
  /** Alguno de los tres que se pudo comparar no cuadra. */
  pipsDisagree: boolean;
};

/**
 * Compara los pips escritos con los que salen de los precios, campo a campo.
 * Un `null` en un lado significa que no hay con que comparar, y eso no es un
 * desacuerdo: solo se juzga lo que esta por partida doble.
 */
export function disagreement(typed: PipsTriplet, derived: PipsTriplet): Disagreement {
  const compare = (a: number | null, b: number | null): boolean =>
    a !== null && b !== null && pipsDisagree(a, b);

  const riskDisagree = compare(typed.plannedRiskPips, derived.plannedRiskPips);
  const rewardDisagree = compare(typed.plannedRewardPips, derived.plannedRewardPips);
  const resultDisagree = compare(typed.resultPips, derived.resultPips);

  return {
    riskDisagree,
    rewardDisagree,
    resultDisagree,
    pipsDisagree: riskDisagree || rewardDisagree || resultDisagree,
  };
}
